import React, { type DependencyList, type FC, type PropsWithChildren, type ReactNode, useEffect } from 'react'

import {
    areArrayEquals,
    assert,
    isArrayNotEmpty,
    normalize,
    objectEntries,
    objectKeys,
    type ReadonlyNonEmptyArray,
    type ReadonlyRecord,
    search,
} from '../../../../utils'

import { applyColumnView, type ColumnView, ConnectwareError, ConnectwareErrorType, defaultColumnView } from '../../../../domain'

import { createExtendedState, type Dispatcher, type Filter, type Selector } from '../../utils'
import type { Column, Columns, CustomizedState, Line, Pagination, Props, Selection, SortOrder, TableError, Translations } from './Types'

/**
 * This state provides internal usage of the props passed to the Table component
 */
const PropsState = createExtendedState<Props<Line>>({ ignorePropsChanges: false })
const useProp = PropsState.useExtendedState as <L extends Line, R>(selector: Selector<Props<L>, R>, filterOrDependencies?: Filter<R> | DependencyList) => R
const usePropDispatcher = PropsState.useExtendedStateDispatcher as <L extends Line>() => ReturnType<Dispatcher<Props<L>>>

const selectSearch = ({ search }: Pick<Props<Line>, 'search'>): string => (typeof search !== 'boolean' && search) || ''
const selectSearchThreshold = ({ searchThreshold }: Pick<Props<Line>, 'searchThreshold'>): number => searchThreshold ?? 0
/** A search shorter than the threshold isn't applied: the rows stay unfiltered */
const selectAppliedSearch = (s: Pick<Props<Line>, 'search' | 'searchThreshold'>): string => {
    const search = selectSearch(s)
    return search.length < selectSearchThreshold(s) ? '' : search
}
const selectPageSizeOptions = ({ pagination }: Pick<Props<Line>, 'pagination'>): ReadonlyNonEmptyArray<number> | null => pagination?.pageSizeOptions ?? null
const selectPageSize = (s: Pick<Props<Line>, 'pagination'>): number | null => s.pagination?.pageSize ?? selectPageSizeOptions(s)?.[0] ?? null
const selectPage = <L extends Line>(s: Props<L>): number => s.pagination?.page ?? 0
const selectSortOrder = <L extends Line>(s: Props<L>): SortOrder<L> | null => s.sortOrder ?? null
const selectSelection = <L extends Line>(s: Props<L>): Props<L>['selection'] => s.selection
const selectHasSelection = <L extends Line>(s: Props<L>): boolean => Boolean(selectSelection(s))
/** The row actions and the column view menu share it */
const selectHasTrailingColumn = <L extends Line>(s: Props<L>): boolean => selectHasSelection(s) || Boolean(s.onColumnViewChange)
/** The rendered columns, in the column view's order and without its hidden columns, if any */
const selectColumnNames = <L extends Line>({ columns, columnView }: Props<L>): readonly (keyof Columns<L>)[] => {
    const names = objectKeys(columns)
    return columnView ? applyColumnView(names, columnView) : names
}
const selectRequiredProperty = <L extends Line, T>(s: Props<L>, selector: (props: Props<L>) => T | undefined, name: string): T => {
    const property = selector(s)
    assert(property, new ConnectwareError(ConnectwareErrorType.STATE, 'Property is not setup', { name }))
    return property
}
const selectRequiredSelection = <L extends Line>(s: Props<L>): Selection<L> => selectRequiredProperty(s, selectSelection, 'selection')
const selectRequiredPagination = (s: Props<Line>): Pagination => selectRequiredProperty(s, (s) => s.pagination, 'pagination')

/**
 * By default we sort the data on FE
 * Unless we explicitly tell not to sort for the tables where data comes from BE presorted
 */
const selectIsSortData = <L extends Line>({ sortData }: Props<L>): boolean => sortData ?? true

/** A column's width while its grip is dragged, rendered before it's committed to the column view */
type ResizingColumn = Readonly<{ name: string, width: number, minWidth: number }>
/**
 * This state is used as an auxiliar variable to the props state
 * It allows for the user to perform custom actions that need to be stored internally
 * and also to calculate certain information that might be too costly to do on every render
 */
type TableAuxState<L extends Line> = Readonly<{ selectedIds: (keyof L & string)[], visibleData: readonly L[], resizing: ResizingColumn | null }>
const AuxState = createExtendedState<TableAuxState<Line>>({ ignorePropsChanges: true })
const useAux = AuxState.useExtendedState as <L extends Line, R>(selector: Selector<TableAuxState<L>, R>, filterOrDependencies?: Filter<R> | DependencyList) => R
const useAuxDispatcher = AuxState.useExtendedStateDispatcher as <L extends Line>() => ReturnType<Dispatcher<TableAuxState<L>>>

const selectSelectedIds = (s: TableAuxState<Line>): TableAuxState<Line>['selectedIds'] => s.selectedIds

class VisibleDataMapper<L extends Line> {
    private static readonly directionMap: ReadonlyRecord<SortOrder<never>['direction'], number> = { asc: 1, desc: -1 }

    private static search (value: unknown, normalizedTerms: readonly string[]): boolean {
        return search(normalize(String(value)), normalizedTerms)
    }

    constructor (private readonly props: Props<L>) {}

    private mapFiller (): L[] {
        const { data, pagination } = this.props

        /** Offset filler for external pagination */
        return pagination &&
                typeof pagination.totalCount === 'number' &&
                typeof pagination.pageSize === 'number' &&
                typeof pagination.page === 'number' &&
                isArrayNotEmpty(data)
            ? Array<L>(pagination.page * pagination.pageSize).fill(data[0])
            : []
    }

    private getFilteredData (): L[] {
        const { data, columns } = this.props

        const search = selectAppliedSearch(this.props)
        const filler = this.mapFiller()

        if (!search) {
            return [...filler, ...data]
        }

        const normalizedTerms = normalize(search).trim().split(/\s+/)

        return [
            ...filler,
            ...data.filter((row) =>
                objectEntries(columns as ReadonlyRecord<string, Column<unknown, unknown>>).some(([name]) =>
                    VisibleDataMapper.search(row[name], normalizedTerms)
                )
            ),
        ]
    }

    private getSortedData (): L[] {
        const data = this.getFilteredData()
        const sortOrder = selectSortOrder(this.props)
        const { columns } = this.props

        if (!sortOrder) {
            return data
        }

        const configuredSorter = columns[sortOrder.name]?.sort
        const sortValueExtractor = typeof configuredSorter === 'function' ? configuredSorter : (v: L[keyof L]) => String(v ?? '')

        return data.sort((a, b) => {
            const { name, direction } = sortOrder
            const compA = sortValueExtractor(a[name])
            const compB = sortValueExtractor(b[name])

            if (compA === compB) {
                return 0
            }

            return VisibleDataMapper.directionMap[direction] * (compA < compB ? -1 : 1)
        })
    }

    map (): readonly L[] {
        const data = selectIsSortData(this.props) ? this.getSortedData() : this.getFilteredData()
        const pageSize = selectPageSize(this.props)
        const page = selectPage(this.props)
        return typeof pageSize === 'number' ? data.slice(page * pageSize, page * pageSize + pageSize) : data
    }
}

const VisibleDataUpdater: FC = () => {
    const visibleData = useProp(
        (s) => new VisibleDataMapper(s).map(),
        (a, b) => areArrayEquals(a, b, { sort: false })
    )
    const dispatch = useAuxDispatcher()

    useEffect(() => dispatch({ visibleData }), [visibleData])

    return null
}

export const PropsProvider = <L extends Line>({ value, children }: PropsWithChildren<{ value: Props<L> }>): ReturnType<FC> => (
    <PropsState.Provider value={value as Props<Line>}>
        <AuxState.Provider value={{ selectedIds: [], visibleData: [], resizing: null }}>
            <VisibleDataUpdater />
            {children}
        </AuxState.Provider>
    </PropsState.Provider>
)

/**
 * - `error`: the data failed to load, the error block is shown whatever the other props say
 * - `rows`: loaded, the rows (or the empty message) are shown
 * - `skeleton`: loading without any rows to show, including when the current search filters out all the rows the table still has
 * - `reloading`: loading while the rows the table already shows stay on screen
 */
type BodyState = 'error' | 'rows' | 'skeleton' | 'reloading'
export const useBodyState = (): BodyState =>
    useProp((s) => {
        if (s.error) {
            return 'error'
        }

        if (!s.loading) {
            return 'rows'
        }

        return isArrayNotEmpty(new VisibleDataMapper(s).map()) ? 'reloading' : 'skeleton'
    })
/** Only while the body shows loaded rows: while loading, they're the last loaded ones, and an error shows none */
export const useShowsLoadedRows = (): boolean => useBodyState() === 'rows'
export const useError = (): TableError | null => useProp((s) => s.error ?? null)
export const useHeight = (): Props<never>['height'] => useProp((s) => s.height)
/**
 * Will return an infinite number if there is no page size available
 */
export const useTotalPages = (): number =>
    useProp((s) => {
        const pageSize = selectPageSize(s)
        if (pageSize === null) {
            return Infinity
        }
        const rowCount = s.pagination?.totalCount ?? s.data.length
        return Math.ceil(rowCount / pageSize)
    })

export const useFilters = (): ReactNode => useProp((s) => s.filters)
export const useUtilities = (): ReactNode => useProp((s) => s.utilities)
export const useHideJumpToPage = (): boolean => useProp((s) => Boolean(s.pagination?.hideJumpToPage))
export const useShowTotalCount = (): boolean => useProp((s) => Boolean(s.pagination?.showTotalCount))
export const useOnDownloadCSV = (): VoidFunction | void => useProp((s) => s.onDownloadCSV)
export const useOnRowClick = <L extends Line>(): ((line: L) => void) | null => useProp((s) => s.onRowClick ?? null)
export const useOnRowOpenInNewTab = <L extends Line>(): ((line: L) => void) | null => useProp((s) => s.onRowOpenInNewTab ?? null)
export const useRowRef = <L extends Line>(): ((line: L, row: HTMLTableRowElement | null) => void) | null => useProp((s) => s.rowRef ?? null)
export const useIsRowActive = <L extends Line>(row: L): boolean => useProp<L, boolean>((s) => s.isRowActive?.(row) ?? false, [row])
export const useIsSearching = (): boolean => useProp((s) => s.search !== false)
export const useSearchThreshold = (): number => useProp(selectSearchThreshold)
/** How many more characters the typed search needs before it's applied; 0 when the field is empty or the search already applies */
export const useSearchCharactersLeft = (): number =>
    useProp((s) => {
        const length = selectSearch(s).length
        return length > 0 ? Math.max(selectSearchThreshold(s) - length, 0) : 0
    })
export const useSearch = (): string => useProp(selectSearch)
export const useAppliedSearch = (): string => useProp(selectAppliedSearch)
export const useTranslations = (): Translations => useProp((s) => s.translations) ?? {}
export const useShouldShowHeader = (): boolean => useProp((s) => !('hideHeader' in s && s.hideHeader))
export const useSortOrder = <L extends Line>(): SortOrder<L> | null => useProp(selectSortOrder)
export const useHasSelection = (): boolean => useProp(selectHasSelection)
export const useHasTrailingColumn = (): boolean => useProp(selectHasTrailingColumn)
/** The rendered columns, plus the checkbox column of a table with selection and the trailing column */
export const useColspan = (): number => useProp((s) => Number(selectHasSelection(s)) + selectColumnNames(s).length + Number(selectHasTrailingColumn(s)))
export const usePageSize = (): number | null => useProp(selectPageSize)
export const usePage = (): number => useProp(selectPage)
export const usePageSizeOptions = (): ReadonlyNonEmptyArray<number> | null => useProp(selectPageSizeOptions)
export const useColumnNames = <L extends Line>(): readonly (keyof Columns<L>)[] =>
    useProp<L, readonly (keyof Columns<L>)[]>(selectColumnNames, (a, b) => areArrayEquals(a, b, { sort: false }))
/** Every column, in the `columns` order, whether the column view renders it or not */
export const useAllColumnNames = <L extends Line>(): readonly (keyof Columns<L>)[] =>
    useProp<L, readonly (keyof Columns<L>)[]>(
        (s) => objectKeys(s.columns),
        (a, b) => areArrayEquals(a, b, { sort: false })
    )
export const useColumns = <L extends Line>(): Columns<L> => useProp<L, Columns<L>>((s) => s.columns)
export const useColumnView = (): ColumnView => useProp((s) => s.columnView ?? defaultColumnView)
export const useOnColumnViewChange = (): Props<Line>['onColumnViewChange'] => useProp((s) => s.onColumnViewChange)
/** Only a table the user can arrange sizes its columns and keeps its cells to one line */
export const useHasColumnView = (): boolean => useProp((s) => Boolean(s.onColumnViewChange))
export const useColumn = <L extends Line, N extends keyof Columns<L>>(name: N): Column<L[N], L> => useColumns<L>()[name]!
export const useVisibleRows = <L extends Line>(): readonly L[] => useAux<L, readonly L[]>((s) => s.visibleData)
/** Whether the body shows data rows, rather than the error block, the skeleton or the empty message */
export const useShowsRows = (): boolean => {
    const bodyState = useBodyState()
    const visibleRows = useVisibleRows()
    return (bodyState === 'rows' || bodyState === 'reloading') && isArrayNotEmpty(visibleRows)
}
export const useResizingColumn = (): ResizingColumn | null => useAux((s) => s.resizing)
export const useResizingColumnDispatcher = (): (resizing: ResizingColumn | null) => void => {
    const dispatch = useAuxDispatcher()
    return (resizing) => dispatch({ resizing })
}

const useSelectionIdField = <L extends Line>(): Selection<L>['idField'] | null => useProp((s) => selectSelection(s)?.idField ?? null)
const useSelectedRows = <L extends Line, T>(mapper: (visibleRows: readonly L[], isSelected: (line: L) => boolean) => T, nonSelected: T): T => {
    const visibleRows = useVisibleRows<L>()
    const idField = useSelectionIdField<L>()
    const selectedIds = useAux(selectSelectedIds)

    if (!selectedIds.length || !idField) {
        return nonSelected
    }

    const selected = new Set(selectedIds)
    return mapper(visibleRows, (line) => selected.has(line[idField] as string))
}

export const useSelected = <L extends Line>(): L[] => useSelectedRows<L, L[]>((rows, isSelected) => rows.filter(isSelected), [])
/** The ids of the selected rows shown, which the toolbar's actions act on */
export const useSelectedIds = (): readonly string[] => {
    const idField = useSelectionIdField()
    const selected = useSelected()
    return idField === null ? [] : selected.map((line) => String(line[idField]))
}
export const useAreThereSelected = <L extends Line>(): boolean => useSelectedRows<L, boolean>((rows, isSelected) => rows.some(isSelected), false)
export const useRequiredSelection = <L extends Line>(): Selection<L> => useProp(selectRequiredSelection)

/**
 * Toggle and dispatcher to be used on the rows checkboxes
 */
export const useSelection = <L extends Line>(row: L): [null, undefined] | [boolean, VoidFunction] => {
    const idField = useSelectionIdField()
    const selectedIds = useAux(selectSelectedIds)
    const dispatch = useAuxDispatcher()

    if (!idField) {
        return [null, undefined]
    }

    const id = row[idField] as string
    const index = selectedIds.indexOf(id)
    const selected = index >= 0

    return [
        selected,
        () =>
            dispatch({
                /**
                 * Either exclude current entry or append it
                 */
                selectedIds: selected ? [...selectedIds.slice(0, index), ...selectedIds.slice(index + 1)] : [...selectedIds, id],
            }),
    ]
}

/**
 * Toggle and dispatcher to be used on the select all checkbox
 */
export const useSelectAll = <L extends Line>(): [null, undefined] | [ReadonlyRecord<'all' | 'onlySome', boolean>, VoidFunction] => {
    const idField = useSelectionIdField<L>()
    const visible = useVisibleRows<L>()
    const selectedIds = useAux(selectSelectedIds)
    const dispatch = useAuxDispatcher()

    if (idField === null) {
        return [null, undefined]
    }

    const actuallySelected: string[] = []
    const allIds: string[] = []
    const idsLeft = new Set<string>(selectedIds)

    visible.forEach((row) => {
        const id = row[idField] as string
        if (idsLeft.delete(id)) {
            actuallySelected.push(id)
        }
        allIds.push(id)
    })

    return [
        {
            all: visible.length > 0 && actuallySelected.length === visible.length,
            onlySome: isArrayNotEmpty(actuallySelected) && actuallySelected.length < visible.length,
        },
        () =>
            dispatch({
                /**
                 * Either exclude current entry or append it
                 */
                selectedIds: isArrayNotEmpty(actuallySelected) ? [] : allIds,
            }),
    ]
}

export const useClearSelection = (): VoidFunction => {
    const dispatch = useAuxDispatcher()
    return () => dispatch({ selectedIds: [] })
}

/**
 * This utility function allows for the creation of hooks that
 * - Signal the onCustomized function
 * - Update the props
 */
const createDispatcherHookWithCustomization = <L extends Line, V>(
    /** Mapper to extract current value from the props */
    mapCurrentValue: (props: Props<L>) => V | unknown,
    /** Map new input, perhaps merging it with current props, into the updated props */
    mapChangedState: (parameter: V, props: Props<L>) => Partial<Props<L>>,
    /** Convert the value into a customized state so it can be passed to onCustomized */
    mapCustomization: (parameter: V) => CustomizedState<L>
): () => (parameter: V) => void =>
() => {
    const onCustomized = useProp<L, Props<L>['onCustomized']>((s) => s.onCustomized)
    const dispatch = usePropDispatcher<L>()
    const currentValue = useProp(mapCurrentValue)

    return (updatedValue) => {
        if (currentValue === updatedValue) {
            return
        }

        dispatch((props) => mapChangedState(updatedValue, props))

        if (onCustomized) {
            onCustomized(mapCustomization(updatedValue))
        }
    }
}

export const useSearchDispatcher = createDispatcherHookWithCustomization<Line, string>(
    selectSearch,
    (search) => ({ search }),
    (search) => ({ search })
)

export const useSortOrderDispatcher = createDispatcherHookWithCustomization<Line, SortOrder<Line>>(
    selectSortOrder,
    (sortOrder) => ({ sortOrder }),
    (sortOrder) => ({ sortOrder })
) as <L extends Line>() => (sort: SortOrder<L>) => void

export const usePageSizeDispatcher = createDispatcherHookWithCustomization<Line, number>(
    selectPageSize,
    (pageSize, s) => ({ pagination: { ...selectRequiredPagination(s), pageSize } }),
    (pageSize) => ({ pageSize })
)

export const usePageDispatcher = createDispatcherHookWithCustomization<Line, number>(
    selectPage,
    (page, s) => ({ pagination: { ...selectRequiredPagination(s), page } }),
    (page) => ({ page })
)

export const useTotalCount = (): number | null => useProp((s) => s.pagination?.totalCount) || null
